import React from "react";
import { Lock, UserPlus, UserX } from "lucide-react";
import { Badge, type BadgeProps } from "../../atoms/Badge/index.js";
import { IconTile } from "../../atoms/IconTile/index.js";
import { TruncatedText } from "../TruncatedText/index.js";
import type {
  PeopleFieldChipTone,
  PeopleFieldChipView,
} from "./people-field-chip.js";
import { PeopleFieldAvatar } from "./PeopleFieldAvatar.js";

/* The tag's look per tone: a role is a quiet outline, a grant still to happen
   is information, and no access or not active is the Watch tone (it can be
   fixed, so it is not critical). Never the accent: the tag commits nothing
   (invariant 9). */
const TAG: Record<PeopleFieldChipTone, Pick<BadgeProps, "tone" | "variant">> = {
  neutral: { tone: "neutral", variant: "outline" },
  pending: { tone: "info", variant: "soft" },
  warning: { tone: "watch", variant: "soft" },
};

/* A narrow field keeps the tag's meaning as one glyph in its tone. A role
   or "Not linked" says nothing the row needs at that width, so it goes. */
const TAG_GLYPH_TONE = { pending: "info", warning: "watch" } as const;

export interface PeopleFieldValueRowProps {
  readonly view: PeopleFieldChipView;
  /** The person's id, for their avatar colour. */
  readonly seed: string | undefined;
  /** "Not active", for the glyph a not-active person gets. */
  readonly notActiveLabel: string;
  /** The badge's id: the trigger's description. */
  readonly tagId: string;
}

/**
 * A picked person or a typed name inside the field frame: avatar, the name on
 * ONE line (it shortens with an ellipsis, and only then shows in full in a
 * tooltip on hover), and the access tag, which never wraps. The frame is a
 * size container (`people-field`): below 18rem the tag becomes a glyph with
 * the same words on hover, so the name keeps its room. The badge is the
 * `tagId` node, which the trigger names as its description at every width.
 */
export function PeopleFieldValueRow({
  view,
  seed,
  notActiveLabel,
  tagId,
}: PeopleFieldValueRowProps): React.ReactElement {
  const tag = view.tag;
  const Glyph =
    tag?.tone === "pending"
      ? UserPlus
      : tag?.label === notActiveLabel
        ? UserX
        : Lock;
  return (
    <>
      <span aria-hidden className="inline-flex shrink-0">
        <PeopleFieldAvatar kind={view.avatar} seed={seed} name={view.name} />
      </span>
      {/* flex-1 here, not on TruncatedText's own wrapper, keeps the tag at
          the row end. */}
      <span className="min-w-0 flex-1 font-medium text-pb-text">
        <TruncatedText>{view.name}</TruncatedText>
      </span>
      {tag ? (
        <>
          {/* The trigger's description. When the field is narrow the badge is
              not displayed, and a node `aria-describedby` names directly
              still counts, so the words are read either way. */}
          <Badge
            id={tagId}
            shape="pill"
            size="sm"
            className="hidden shrink-0 @min-[18rem]/people-field:inline-flex"
            {...TAG[tag.tone]}
          >
            {tag.label}
          </Badge>
          {tag.tone === "neutral" ? null : (
            <IconTile
              size="xs"
              shape="circle"
              tone={TAG_GLYPH_TONE[tag.tone]}
              title={tag.label}
              className="shrink-0 @min-[18rem]/people-field:hidden"
            >
              <Glyph />
            </IconTile>
          )}
        </>
      ) : null}
    </>
  );
}
